import { Toast as BaseToast } from '@base-ui/react/toast';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn';

interface ToastProviderProps extends React.ComponentPropsWithoutRef<typeof BaseToast.Provider> {
  /**
   * Maximum number of toasts visible at once.
   * @default 3
   */
  limit?: number;

  /**
   * Default timeout in milliseconds before toasts auto-dismiss.
   * @default 5000
   */
  timeout?: number;
}

/** Must wrap the part of the app that uses toasts. */
function ToastProvider({ limit = 3, timeout = 5000, children, ...props }: ToastProviderProps) {
  return (
    <BaseToast.Provider limit={limit} timeout={timeout} {...props}>
      {children}
    </BaseToast.Provider>
  );
}

type ToastPortalProps = React.ComponentPropsWithoutRef<typeof BaseToast.Portal>;

/** Should contain the Toast.Viewport. */
function ToastPortal({ children, ...props }: ToastPortalProps) {
  return <BaseToast.Portal {...props}>{children}</BaseToast.Portal>;
}

interface ToastViewportProps extends React.ComponentPropsWithoutRef<typeof BaseToast.Viewport> {
  className?: string;
}

function ToastViewport({ className, children, ...props }: ToastViewportProps) {
  return (
    <BaseToast.Viewport
      className={cn(
        'fixed z-50',
        'right-4 bottom-4',
        'flex flex-col',
        'w-full max-w-sm',
        className,
      )}
      {...props}
    >
      {children}
    </BaseToast.Viewport>
  );
}

interface ToastRootProps extends React.ComponentPropsWithoutRef<typeof BaseToast.Root> {
  className?: string;
}

function Toast({ className, children, ...props }: ToastRootProps) {
  return (
    <BaseToast.Root
      className={cn(
        'absolute right-0 bottom-0 left-auto',
        'z-[calc(1000-var(--toast-index))]',
        'w-full origin-bottom',
        '[--gap:0.75rem]',
        '[--peek:0.75rem]',
        '[--scale:calc(max(0,1-(var(--toast-index)*0.1)))]',
        '[--shrink:calc(1-var(--scale))]',
        '[--height:var(--toast-frontmost-height,var(--toast-height))]',
        '[--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))]',
        'transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))]',
        'h-(--height)',
        'bg-background text-foreground rounded-lg border',
        'shadow-lg',
        'select-none',
        "after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
        '[transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]',
        'data-starting-style:transform-[translateY(150%)]',
        'data-ending-style:opacity-0',
        '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:transform-[translateY(150%)]',
        'data-ending-style:data-swipe-direction-down:transform-[translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'data-ending-style:data-swipe-direction-left:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'data-ending-style:data-swipe-direction-right:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'data-ending-style:data-swipe-direction-up:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        'data-limited:opacity-0',
        className,
      )}
      {...props}
    >
      {children}
    </BaseToast.Root>
  );
}

interface ToastContentProps extends React.ComponentPropsWithoutRef<typeof BaseToast.Content> {
  className?: string;
}

function ToastContent({ className, children, ...props }: ToastContentProps) {
  return (
    <BaseToast.Content
      className={cn(
        'p-4',
        'overflow-hidden',
        'transition-opacity duration-250',
        'data-behind:pointer-events-none data-behind:opacity-0',
        'data-expanded:pointer-events-auto data-expanded:opacity-100',
        className,
      )}
      {...props}
    >
      {children}
    </BaseToast.Content>
  );
}

interface ToastTitleProps extends React.ComponentPropsWithoutRef<typeof BaseToast.Title> {
  className?: string;
}

function ToastTitle({ className, ...props }: ToastTitleProps) {
  return (
    <BaseToast.Title
      className={cn('text-foreground text-sm/5 font-medium', className)}
      {...props}
    />
  );
}

interface ToastDescriptionProps extends React.ComponentPropsWithoutRef<
  typeof BaseToast.Description
> {
  className?: string;
}

function ToastDescription({ className, ...props }: ToastDescriptionProps) {
  return (
    <BaseToast.Description
      className={cn('text-muted-foreground mt-1 text-sm/5', className)}
      {...props}
    />
  );
}

interface ToastActionProps extends React.ComponentPropsWithoutRef<typeof BaseToast.Action> {
  className?: string;
}

function ToastAction({ className, ...props }: ToastActionProps) {
  return (
    <BaseToast.Action
      className={cn(
        'mt-3 inline-flex items-center justify-center',
        'h-8 px-3',
        'border-border rounded-md border',
        'text-sm font-medium',
        'bg-background text-foreground',
        'hover:bg-muted',
        'focus:outline-ring focus:outline-2 focus:-outline-offset-1 focus:outline-solid',
        'transition-colors',
        className,
      )}
      {...props}
    />
  );
}

interface ToastCloseProps extends React.ComponentPropsWithoutRef<typeof BaseToast.Close> {
  className?: string;
}

function ToastClose({ className, children, ...props }: ToastCloseProps) {
  return (
    <BaseToast.Close
      className={cn(
        'absolute top-2 right-2',
        'flex size-6 items-center justify-center',
        'rounded-md',
        'border-none bg-transparent',
        'text-muted-foreground hover:text-foreground',
        'hover:bg-muted',
        'focus:outline-ring focus:outline-2 focus:-outline-offset-1 focus:outline-solid',
        'transition-colors',
        className,
      )}
      aria-label="Close"
      {...props}
    >
      {children ?? <X className="size-4" aria-hidden="true" />}
    </BaseToast.Close>
  );
}

export const useToastManager = BaseToast.useToastManager;
export const createToastManager = BaseToast.createToastManager;

export {
  Toast,
  ToastProvider,
  ToastPortal,
  ToastViewport,
  ToastContent,
  ToastTitle,
  ToastDescription,
  ToastAction,
  ToastClose,
};
